import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../AppIcon';

const Footer = () => {
  const currentYear = new Date()?.getFullYear();

  const footerSections = [
    {
      title: 'Platform',
      links: [
        { name: 'Multi-Role Dashboard', path: '/multi-role-dashboard' },
        { name: 'Discovery Engine', path: '/program-discovery-engine' },
        { name: 'Scouting Interface', path: '/real-time-scouting-interface' },
        { name: 'Performance Center', path: '/referral-performance-center' },
        { name: 'Analytics Center', path: '/analytics-insights-center' }
      ]
    },
    {
      title: 'Solutions',
      links: [
        { name: 'For Corporations', path: '#' },
        { name: 'For Startups', path: '#' },
        { name: 'For Intermediaries', path: '#' },
        { name: 'For Field Operations', path: '#' },
        { name: 'Enterprise', path: '#' }
      ]
    },
    {
      title: 'Resources',
      links: [
        { name: 'Documentation', path: '#' },
        { name: 'API Reference', path: '#' },
        { name: 'Success Stories', path: '#' },
        { name: 'Best Practices', path: '#' },
        { name: 'Webinars', path: '#' }
      ]
    },
    {
      title: 'Company',
      links: [
        { name: 'About Us', path: '#' },
        { name: 'Careers', path: '#' },
        { name: 'Press Kit', path: '#' },
        { name: 'Contact', path: '#' },
        { name: 'Partners', path: '#' }
      ]
    },
    {
      title: 'Support',
      links: [
        { name: 'Help Center', path: '#' },
        { name: 'Community', path: '#' },
        { name: 'System Status', path: '#' },
        { name: 'Security', path: '#' },
        { name: 'Privacy Policy', path: '#' }
      ]
    }
  ];

  const socialLinks = [
    { name: 'LinkedIn', icon: 'Linkedin', url: '#' },
    { name: 'Twitter', icon: 'Twitter', url: '#' },
    { name: 'GitHub', icon: 'Github', url: '#' },
    { name: 'YouTube', icon: 'Youtube', url: '#' }
  ];

  const certifications = [
    { name: 'SOC 2 Type II', icon: 'Shield' },
    { name: 'ISO 27001', icon: 'Award' },
    { name: 'GDPR Compliant', icon: 'Lock' },
    { name: 'Enterprise Ready', icon: 'Building' }
  ];

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-6 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2 space-y-6">
            <Link to="/unified-landing-hub" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                <Icon name="Bridge" size={24} color="white" strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="text-xl font-bold">ScoutBridge</h3>
                <p className="text-sm text-slate-400">Innovation Ecosystem</p>
              </div>
            </Link>
            
            <p className="text-slate-400 leading-relaxed">
              The world's most intelligent startup-corporate matching platform. 
              Connecting innovation through verified intermediary networks and 
              real-time field intelligence.
            </p>

            {/* Contact Info */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 text-sm text-slate-400">
                <Icon name="Mail" size={16} />
                <span>hello@scoutbridge.com</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-slate-400">
                <Icon name="Phone" size={16} />
                <span>+1 (555) 123-4567</span>
              </div>
              <div className="flex items-center space-x-2 text-sm text-slate-400">
                <Icon name="MapPin" size={16} />
                <span>San Francisco, CA • London, UK • Singapore</span>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks?.map((social) => (
                <a
                  key={social?.name}
                  href={social?.url}
                  className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center hover:bg-slate-700 transition-colors duration-200"
                  aria-label={social?.name}
                >
                  <Icon name={social?.icon} size={20} />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation Sections */}
          {footerSections?.map((section) => (
            <div key={section?.title} className="space-y-4">
              <h4 className="text-lg font-semibold">{section?.title}</h4>
              <ul className="space-y-2">
                {section?.links?.map((link) => (
                  <li key={link?.name}>
                    <Link
                      to={link?.path}
                      className="text-slate-400 hover:text-white transition-colors duration-200 text-sm"
                    >
                      {link?.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter Signup */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h4 className="text-xl font-semibold mb-2">Stay Updated</h4>
              <p className="text-slate-400">
                Get the latest insights on innovation trends, success stories, and platform updates.
              </p>
            </div>
            <div className="flex space-x-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button className="px-6 py-3 bg-gradient-to-r from-primary to-accent rounded-lg font-medium hover:shadow-lg transition-all duration-200">
                Subscribe
              </button>
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-wrap justify-center md:justify-start items-center gap-6">
            <span className="text-sm text-slate-400 font-medium">Trusted & Certified:</span>
            {certifications?.map((cert) => (
              <div key={cert?.name} className="flex items-center space-x-2 text-slate-400">
                <Icon name={cert?.icon} size={16} />
                <span className="text-sm">{cert?.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-wrap items-center space-x-6 text-sm text-slate-400">
              <span>© {currentYear} ScoutBridge. All rights reserved.</span>
              <Link to="#" className="hover:text-white transition-colors duration-200">
                Terms of Service
              </Link>
              <Link to="#" className="hover:text-white transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="#" className="hover:text-white transition-colors duration-200">
                Cookie Policy
              </Link>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-slate-400">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                <span>All systems operational</span>
              </div>
              <span>•</span>
              <span>Version 2.1.0</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;  